import { offlineDb } from './offline-db';
import { api } from './api';
import type { ApiResponse } from '@trackkm/shared';

function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError && error.message.includes('fetch')) return true;
  if (error instanceof DOMException && error.name === 'AbortError') return true;
  return false;
}

async function queueAction(
  action: Parameters<typeof offlineDb.pendingActions.add>[0]['action'],
  payload: Record<string, unknown>,
): Promise<void> {
  await offlineDb.pendingActions.add({
    action,
    payload,
    createdAt: new Date().toISOString(),
    retryCount: 0,
  });
}

function offlineResponse<T>(data: T): ApiResponse<T> {
  return { success: true, data, error: null };
}

export async function offlineStartTrip(payload: {
  readonly purpose: string;
  readonly clientId?: string;
  readonly startAddress: string;
  readonly startLat?: number;
  readonly startLng?: number;
}): Promise<ApiResponse<unknown>> {
  if (!navigator.onLine) {
    await queueAction('start_trip', { ...payload });
    return offlineResponse({ queued: true });
  }

  try {
    const response = await api.post<unknown>('/trips/start', payload);
    return response;
  } catch (error) {
    if (isNetworkError(error)) {
      await queueAction('start_trip', { ...payload });
      return offlineResponse({ queued: true });
    }
    throw error;
  }
}

export async function offlineStopTrip(payload: {
  readonly tripId: string;
  readonly endAddress?: string;
  readonly endLat?: number;
  readonly endLng?: number;
  readonly employeeNote?: string;
}): Promise<ApiResponse<unknown>> {
  if (!navigator.onLine) {
    await queueAction('stop_trip', { ...payload });
    return offlineResponse({ queued: true });
  }

  try {
    const response = await api.post<unknown>('/trips/stop', payload);
    return response;
  } catch (error) {
    if (isNetworkError(error)) {
      await queueAction('stop_trip', { ...payload });
      return offlineResponse({ queued: true });
    }
    throw error;
  }
}

export async function offlineGpsPing(payload: {
  readonly tripId: string;
  readonly lat: number;
  readonly lng: number;
  readonly accuracy: number | null;
  readonly timestamp: string;
}): Promise<void> {
  if (!navigator.onLine) {
    await offlineDb.cachedGpsPoints.add({
      tripId: payload.tripId,
      lat: payload.lat,
      lng: payload.lng,
      accuracy: payload.accuracy,
      timestamp: payload.timestamp,
      synced: false,
    });
    return;
  }

  try {
    await api.post('/trips/gps-ping', payload);
  } catch (error) {
    if (isNetworkError(error)) {
      await offlineDb.cachedGpsPoints.add({
        tripId: payload.tripId,
        lat: payload.lat,
        lng: payload.lng,
        accuracy: payload.accuracy,
        timestamp: payload.timestamp,
        synced: false,
      });
      return;
    }
    // Non-network errors: still cache the point so data isn't lost
    await offlineDb.cachedGpsPoints.add({
      tripId: payload.tripId,
      lat: payload.lat,
      lng: payload.lng,
      accuracy: payload.accuracy,
      timestamp: payload.timestamp,
      synced: false,
    });
  }
}

export async function offlineApproveTrip(payload: {
  readonly tripId: string;
  readonly note?: string;
}): Promise<ApiResponse<unknown>> {
  if (!navigator.onLine) {
    await queueAction('approve_trip', { ...payload });
    return offlineResponse({ queued: true });
  }

  try {
    const response = await api.post<unknown>(`/approvals/${payload.tripId}/approve`, {
      note: payload.note,
    });
    return response;
  } catch (error) {
    if (isNetworkError(error)) {
      await queueAction('approve_trip', { ...payload });
      return offlineResponse({ queued: true });
    }
    throw error;
  }
}

export async function offlineRejectTrip(payload: {
  readonly tripId: string;
  readonly note?: string;
}): Promise<ApiResponse<unknown>> {
  if (!navigator.onLine) {
    await queueAction('reject_trip', { ...payload });
    return offlineResponse({ queued: true });
  }

  try {
    const response = await api.post<unknown>(`/approvals/${payload.tripId}/reject`, {
      note: payload.note,
    });
    return response;
  } catch (error) {
    if (isNetworkError(error)) {
      await queueAction('reject_trip', { ...payload });
      return offlineResponse({ queued: true });
    }
    throw error;
  }
}
